import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import InfoIconOutlined from "@mui/icons-material/InfoOutlined";
import type { ReactNode } from "react";
import { useState } from "react";
import { HintDetailModal } from "Components/Shared/ValueTile/Components/HintDetail/HintDetailModal";

type Props = {
  icon: ReactNode;
  title: string;
  value: string;
  explanationHint: ReactNode;
  variant?: "basic" | "space";
  isLoading?: boolean;
};

export const ValueTile = ({
  icon,
  title,
  value,
  explanationHint,
  variant = "basic",
  isLoading = false,
}: Props) => {
  const [isHintOpen, setIsHintOpen] = useState(false);

  const isSpaceVariant = variant === "space";
  const color = isSpaceVariant ? "common.white" : "text.primary";

  return (
    <Box
      display="flex"
      alignItems="center"
      py={2}
      px={3}
      borderRadius={2}
      sx={{
        opacity: isLoading ? 0.3 : 1,
        ...(variant === "basic" && {
          backgroundColor: "background.paper",
          border: 1,
          borderColor: "divider",
        }),
        ...(isSpaceVariant && {
          backgroundColor: "common.black",
          backgroundImage: "url('/images/space-background.svg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }),
      }}
    >
      <Box display="flex" alignItems="center" width="100%">
        <Box display="flex" mr={2} sx={{ "& svg": { fontSize: 24, color } }}>
          {icon}
        </Box>
        <Box flex={1} mr={2}>
          <Typography
            variant="body1"
            component="span"
            fontWeight={600}
            color={color}
          >
            {title}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {isLoading ? (
            <Box minHeight={32} display="flex" alignItems="center">
              <CircularProgress size={16} sx={{ color: "grey.300" }} />
            </Box>
          ) : (
            <Typography
              variant="h6"
              component="span"
              fontWeight={600}
              color={color}
            >
              {value}
            </Typography>
          )}
          {explanationHint && (
            <IconButton
              size="small"
              onClick={() => setIsHintOpen(true)}
              aria-label="Show information"
              sx={{ color, padding: 0.5 }}
            >
              <InfoIconOutlined sx={{ mt: 0.25, fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>
      {explanationHint && (
        <HintDetailModal
          isOpen={isHintOpen}
          onClose={() => setIsHintOpen(false)}
          content={<Box mt={4}>{explanationHint}</Box>}
          title={title}
        />
      )}
    </Box>
  );
};
